import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import type {
  LineItem,
  InboundRequest,
  OpsComment,
  OrderSummary,
  AuditHistoryEntryInput,
  CloneOrderRequest,
  OrderUpdateRequest,
  OrderAddress,
} from '../../../../infrastructure/OMSClient';
import { OrderDetail } from './OrderDetail';
import { Button, Row, Col, OverlayTrigger, Tooltip, Modal, Form } from 'react-bootstrap';
import { ArrowLeft, Copy, History, Trash, ExternalLink } from 'lucide-react';
import { useOMS } from '../../../../apps/hooks/oms/useOMS';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../../../providers/AuthProvider';
import { getConfig } from '../../../../config';

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [order, setOrder] = useState<OrderSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stockStatusBySku, setStockStatusBySku] = useState<Record<string, number>>({});
  const oms = useOMS();
  const fetchingStockStatusRef = useRef(false);
  const fetchingOrderRef = useRef(false);
  const { user } = useAuth();

  // Add state for cancel order modal
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelValid, setCancelValid] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  // Add state for clone order modal
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneOrderId, setCloneOrderId] = useState('');
  const [cloneDeleoOrderId, setCloneDeleoOrderId] = useState('');
  const [isCloning, setIsCloning] = useState(false);

  // Create audit entry helper function
  const createAuditEntry = (): AuditHistoryEntryInput => ({
    operator: user?.name || 'Unknown',
    source: 'OPERATOR',
  });

  const fetchOrder = async () => {
    if (fetchingOrderRef.current || !id) {
      return;
    }

    try {
      setLoading(true);
      fetchingOrderRef.current = true;
      const orderData = await oms.getOrderDetail(id);
      setOrder(orderData);
      setError(null);
    } catch (err) {
      setError('Failed to fetch order details');
      console.error('Error fetching order:', err);
    } finally {
      setLoading(false);
      setTimeout(() => {
        fetchingOrderRef.current = false;
      }, 1000); // release the lock after 1 second
    }
  };

  useEffect(() => {
    if (id) {
      fetchOrder();
    }
  }, [id]);

  const handleUpdateLineItem = async (lineItem: LineItem) => {
    if (!order) {
      return;
    }

    try {
      // Check if this is a new item (no itemId) or an existing one
      const isNewItem = !lineItem.itemId;

      const result = await oms.updateOrder(order.orderId, {
        lineItems: [lineItem],
        auditEntry: createAuditEntry(),
      });

      if (result) {
        setOrder(result);
        toast.success(isNewItem ? 'Line item added successfully' : 'Line item updated successfully');
      }
    } catch (err) {
      console.error('Error updating line item:', err);
    }
  };

  const handleUpdateComments = async (opsComments: OpsComment[]) => {
    if (!order) {
      return;
    }

    try {
      const request: OrderUpdateRequest = {
        opsComments: opsComments,
        auditEntry: createAuditEntry(),
      };

      const result = await oms.updateOrder(order.orderId, request);
      if (result) {
        setOrder(result);
      }
    } catch (err) {
      console.error('Error updating comments:', err);
    }
  };

  const handleUpdatePic = async (pic: string) => {
    if (!order) {
      return;
    }

    try {
      const request: OrderUpdateRequest = {
        pic: pic,
        auditEntry: createAuditEntry(),
      };

      const result = await oms.updateOrder(order.orderId, request);
      if (result) {
        setOrder(result);
        toast.success('PIC updated successfully');
      }
    } catch (err) {
      console.error('Error updating PIC:', err);
      toast.error('Failed to update PIC');
    }
  };

  const handleUpdateBillingAddress = async (billingAddress: OrderAddress) => {
    if (!order) {
      return;
    }

    try {
      const request: OrderUpdateRequest = {
        billingAddress: billingAddress,
        auditEntry: createAuditEntry(),
      };

      const result = await oms.updateOrder(order.orderId, request);
      if (result) {
        setOrder(result);
        toast.success('Billing address updated successfully');
      }
    } catch (err) {
      console.error('Error updating billing address:', err);
      toast.error('Failed to update billing address');
    }
  };

  const handleUpdateShippingAddress = async (shippingAddress: OrderAddress) => {
    if (!order) {
      return;
    }

    try {
      const request: OrderUpdateRequest = {
        shippingAddress: shippingAddress,
        auditEntry: createAuditEntry(),
      };

      const result = await oms.updateOrder(order.orderId, request);
      if (result) {
        setOrder(result);
        toast.success('Shipping address updated successfully');
      }
    } catch (err) {
      console.error('Error updating shipping address:', err);
      toast.error('Failed to update shipping address');
    }
  };

  const handleRequestInbound = async (inboundRequest: Omit<InboundRequest, 'auditEntry'>) => {
    try {
      console.log('inboundRequest', inboundRequest);

      // Add audit entry to the inbound request
      const requestWithAudit: InboundRequest = {
        ...inboundRequest,
        auditEntry: createAuditEntry(),
      };

      const response = await oms.requestInbound(id!, requestWithAudit);
      if (response) {
        await fetchOrder(); // Using the locked fetchOrder function
        toast.success(`Inbound request created successfully`);
      }
      console.log('response', response);
    } catch (error) {
      console.error('Failed to request inbound:', error);
      toast.error(`Failed to request inbound: ${error}`);
    }
  };

  const handleCancelInbound = async (inboundId: string) => {
    try {
      const response = await oms.cancelInbound(id!, inboundId, createAuditEntry());
      if (response) {
        await fetchOrder();
        toast.success('Successfully cancelled inbound request');
      }
      console.log('response', response);
    } catch (error) {
      console.error('Error cancelling inbound:', error);
      toast.error('Failed to cancel inbound request: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const handleCancelOrder = async () => {
    if (!cancelReason.trim() || !user) {
      setCancelValid(false);
      return;
    }

    setIsCancelling(true);

    try {
      const response = await oms.cancelOrder(
        id!,
        {
          reason: 'Cancellation reason: ' + cancelReason.trim(),
          operator: user.name,
        },
        createAuditEntry(),
      );

      if (response) {
        await fetchOrder();
        toast.success('Order cancelled successfully');
        setShowCancelModal(false);
        setCancelReason('');
      }
    } catch (error) {
      console.error('Error cancelling order:', error);
      toast.error('Failed to cancel order: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsCancelling(false);
    }
  };

  const openCancelModal = () => {
    if (!user) {
      toast.error('You must be logged in to cancel an order');
      return;
    }
    setShowCancelModal(true);
    setCancelValid(false);
    setCancelReason('');
  };

  const openCloneModal = () => {
    if (!user) {
      toast.error('You must be logged in to clone an order');
      return;
    }
    // Suggest new order ID by appending -1 to original
    const suggestedOrderId = `${order?.orderId}-1`;
    setCloneOrderId(suggestedOrderId);
    setCloneDeleoOrderId('');
    setShowCloneModal(true);
  };

  const handleCloneOrder = async () => {
    if (!cloneOrderId.trim() || !user || !order) {
      return;
    }

    setIsCloning(true);

    try {
      const cloneOrderRequest: CloneOrderRequest = {
        originalOrderId: order.orderId!,
        newOrderId: cloneOrderId.trim()!,
        newDeleoOrderId: cloneDeleoOrderId.trim() || '',
        auditEntry: createAuditEntry(),
      };
      const response = await oms.cloneOrder(cloneOrderRequest);

      if (response) {
        // Show success message with clickable order number
        const toastId = toast.success(
          <div>
            Order cloned successfully!
            <br />
            <button
              onClick={() => {
                toast.dismiss(toastId);
                navigate(`/oms/orders/${response.orderId}`);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#0066cc',
                textDecoration: 'underline',
                cursor: 'pointer',
                padding: 0,
                fontSize: 'inherit',
              }}
            >
              Click here to view Order #{response.orderId}
            </button>
          </div>,
          { duration: 6000 },
        );
        setShowCloneModal(false);
        setCloneOrderId('');
        setCloneDeleoOrderId('');

        // Don't navigate immediately, let user choose via the toast
      }
    } catch (error) {
      console.error('Error cloning order:', error);
      toast.error('Failed to clone order: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsCloning(false);
    }
  };

  const handleGetStockStatus = async (skus: string[]) => {
    if (!order || !order.lineItems || fetchingStockStatusRef.current) {
      return;
    }

    try {
      // get stock status for each sku
      // Remove duplicate SKUs before making the API call
      const uniqueSkus = Array.from(new Set(skus));
      fetchingStockStatusRef.current = true;
      const stockStatus = await oms.getStockStatus(uniqueSkus.join(','));
      if (stockStatus) {
        const stockStatusBySku = stockStatus.items.reduce(
          (acc, item) => {
            acc[item.sku] = item.quantity;
            return acc;
          },
          {} as Record<string, number>,
        );
        setStockStatusBySku(stockStatusBySku);
      }
    } catch (error) {
      console.error('Error getting stock status:', error);
    } finally {
      setTimeout(() => {
        fetchingStockStatusRef.current = false;
      }, 1000); // release the lock after 1 second to prevent concurrent fetching
    }
  };

  const handleRetryDeleoOrder = async () => {
    if (!order || !order.orderId) {
      return;
    }

    toast.success('Creating Deleo order...');

    try {
      // Create Deleo order for existing order using OMS client
      const response: OrderSummary | null = await oms.createDeleoOrder(order.orderId, createAuditEntry());
      console.log('response', response);
      if (response) {
        setOrder(response);
        toast.success('Deleo order created successfully');
      } else {
        toast.error('Failed to create Deleo order');
      }
    } catch (error) {
      console.error('Error creating Deleo order:', error);
      toast.error('Error creating Deleo order: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const handleResetDeleoOrder = async () => {
    if (!order || !order.orderId || !user) {
      return;
    }

    try {
      toast.loading('Resetting Deleo order...');
      const response = await oms.resetDeleoOrder(order.orderId, createAuditEntry());
      if (response) {
        await fetchOrder();
        toast.dismiss();
        toast.success('Deleo order reset successfully');
      } else {
        toast.dismiss();
        toast.error('Failed to reset Deleo order');
      }
    } catch (error) {
      console.error('Error resetting Deleo order:', error);
      toast.dismiss();
      toast.error('Error resetting Deleo order: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const handleResetParxlOrder = async () => {
    if (!order || !order.orderId || !user) {
      return;
    }

    try {
      toast.loading('Resetting Parxl order...');
      const response = await oms.resetParxlOrder(order.orderId, createAuditEntry());
      if (response) {
        await fetchOrder();
        toast.dismiss();
        toast.success('Parxl order reset successfully');
      } else {
        toast.dismiss();
        toast.error('Failed to reset Parxl order');
      }
    } catch (error) {
      console.error('Error resetting Parxl order:', error);
      toast.dismiss();
      toast.error('Error resetting Parxl order: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  if (!order) {
    return <div>Order not found</div>;
  }

  return (
    <div className="space-y-6">
      <Row>
        <Col xs={6} className="text-start">
          <Button
            variant="outline-primary"
            onClick={() => navigate('/oms/orders', { state: { page: location.state?.page || 1 } })}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Orders
          </Button>
        </Col>

        <Col xs={6} className="text-end">
          <span className="me-4">
            <Button variant="outline-secondary" onClick={() => navigate(`/oms/orders/${id}/audit`)}>
              <History className="h-4 w-4 me-2" />
              Audit History
            </Button>
          </span>
          <span className="me-4">
            <Button
              variant="outline-info"
              onClick={() => {
                const config = getConfig();
                const wpUrl = config.wpAdminUrl.replace(/\/$/, '');
                window.open(`${wpUrl}/admin.php?page=wc-orders&action=edit&id=${id}`, '_blank');
              }}
            >
              <ExternalLink className="h-4 w-4 me-2" />
              View in WP
            </Button>
          </span>
          <span className="me-4">
            <Button variant="outline-primary" onClick={openCloneModal}>
              <Copy className="h-4 w-4 me-2" />
              Clone Order
            </Button>
          </span>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                {['cancelled', 'shipped', 'completed'].includes(order.status)
                  ? `This order is already ${order.status}`
                  : order.status !== 'processing'
                    ? "Only orders in 'processing' status can be cancelled"
                    : order.deleoData?.deleoStatus?.name &&
                        !['REQUEST_ORDER', 'REQUEST_OUTBOUND'].includes(order.deleoData.deleoStatus.name)
                      ? "Only orders with Deleo status 'REQUEST_ORDER' or 'REQUEST_OUTBOUND' can be cancelled"
                      : ''}
              </Tooltip>
            }
          >
            <span>
              <Button
                variant="outline-danger"
                disabled={
                  ['cancelled', 'shipped', 'completed'].includes(order.status) ||
                  order.status !== 'processing' ||
                  (order.deleoData?.deleoStatus?.name !== undefined &&
                    !['REQUEST_ORDER', 'REQUEST_OUTBOUND'].includes(order.deleoData.deleoStatus.name))
                }
                onClick={openCancelModal}
              >
                <Trash className="h-4 w-4 me-2" />
                Cancel Order
              </Button>
            </span>
          </OverlayTrigger>
        </Col>
      </Row>

      {/* Cancel Order Modal */}
      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Cancel Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Please provide a reason for canceling this order:</p>
          <Form.Group>
            <Form.Label>
              Cancellation Reason <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={cancelReason}
              onChange={(e) => {
                setCancelReason(e.target.value);
                setCancelValid(e.target.value.trim().length > 0);
              }}
              required
              placeholder="Enter detailed reason for cancellation"
            />
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Operator</Form.Label>
            <Form.Control type="text" value={user?.name || ''} disabled />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCancelModal(false)} disabled={isCancelling}>
            Close
          </Button>
          <Button variant="danger" onClick={handleCancelOrder} disabled={!cancelValid || isCancelling}>
            {isCancelling ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Cancelling...
              </>
            ) : (
              'Confirm Cancellation'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Clone Order Modal */}
      <Modal show={showCloneModal} onHide={() => setShowCloneModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Clone Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-warning" role="alert">
            <strong>⚠️ Important Warning:</strong>
            <ul className="mb-0 mt-2">
              <li>This is an internal operational order for shipping issues.</li>
              <li>
                This cloned order will <strong>NOT</strong> be synced with WooCommerce, and no WooCommerce order will be
                created.
              </li>
              <li>
                Before cloning, please check if the Deleo order has already been created. Using mismatched Deleo order
                will cause a system error.
              </li>
              <li>
                After cloning, please check the Deleo Portal to confirm the Deleo order is synced up with the cloned
                order.
              </li>
            </ul>
          </div>

          <p>Please provide a new order ID for the cloned order. The original order ID is {order?.orderId}.</p>
          <Form.Group>
            <Form.Label>
              New Order ID <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={cloneOrderId}
              onChange={(e) => setCloneOrderId(e.target.value)}
              required
              placeholder="Enter new order ID"
            />
            <Form.Text className="text-danger">Recommended: {order?.orderId}-1</Form.Text>
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Deleo Order ID (Optional)</Form.Label>
            <Form.Control
              type="text"
              value={cloneDeleoOrderId}
              onChange={(e) => setCloneDeleoOrderId(e.target.value)}
              placeholder="Enter Deleo order ID if exists"
            />
            <Form.Text className="text-danger">Leave empty if no Deleo order exists yet.</Form.Text>
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Operator</Form.Label>
            <Form.Control type="text" value={user?.name || ''} disabled />
            <Form.Text className="text-danger">This will be logged for audit purpose.</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCloneModal(false)} disabled={isCloning}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCloneOrder} disabled={!cloneOrderId.trim() || isCloning}>
            {isCloning ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Cloning...
              </>
            ) : (
              'Clone Order'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <OrderDetail
        order={order}
        onUpdateLineItem={handleUpdateLineItem}
        onRequestInbound={handleRequestInbound}
        onUpdateComments={handleUpdateComments}
        onUpdatePic={handleUpdatePic}
        onUpdateBillingAddress={handleUpdateBillingAddress}
        onUpdateShippingAddress={handleUpdateShippingAddress}
        onGetStockStatus={handleGetStockStatus}
        stockStatusBySku={stockStatusBySku}
        onRetryDeleoOrder={handleRetryDeleoOrder}
        onResetDeleoOrder={handleResetDeleoOrder}
        onResetParxlOrder={handleResetParxlOrder}
        onCancelInbound={handleCancelInbound}
        onRefreshOrder={fetchOrder}
      />
    </div>
  );
}
